/* Customer reviews.
 *
 * Two jobs in one file:
 *   1. the product page: fetch the product's reviews from the public endpoint
 *      Judge.me's own widget calls and draw them as tiles (the app block stays
 *      on the page, hidden, for the JSON-LD and the write-review form);
 *   2. every strip of tiles, product page or home page: move it along one tile
 *      at a time, and open a tile's full review when it is tapped.
 *
 * The home page's tiles are rendered in Liquid from the shop metafields, with
 * the same classes, so one stylesheet and this one driver serve both.
 */
(() => {
  /* i18n (P1, 24 Sep 2026): visible strings come from the stk-i18n-reviews island the
     section renders next to this script, read through window.stkT. The second argument is
     the English text this file used before, so a page without the island is unchanged. */
  const stkT = window.stkT || ((k, f) => f);
  const ENDPOINT = 'https://cdn.judge.me/reviews/reviews_for_widget';
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* Judge.me sends body_html already sanitised, but it is still third-party
     HTML: allow the handful of tags a review can legitimately contain. */
  const clean = (html) => {
    let src = html || '';
    /* some shop replies arrive with their tags already escaped, so they would
       print as literal <p> on the card. Decode once before sanitising. */
    if (src.includes('&lt;')) { const ta = document.createElement('textarea'); ta.innerHTML = src; src = ta.value; }
    /* P172: parsed in a template, which is inert: nothing in it loads or runs before it is cleaned */
    const d = document.createElement('template');
    d.innerHTML = src;
    d.content.querySelectorAll('*').forEach((el) => {
      if (!/^(P|BR|EM|STRONG|B|I|UL|OL|LI|SPAN)$/.test(el.tagName)) el.replaceWith(...el.childNodes);
      else [...el.attributes].forEach((a) => el.removeAttribute(a.name));
    });
    return d.innerHTML;
  };
  const strip = (html) => { const d = document.createElement('template'); d.innerHTML = html || ''; return (d.content.textContent || '').trim(); };   // P172: inert parse

  /* ---- reviews in the page language (P172, 2 Oct 2026) -------------------
     Our translations come from JSON islands (script[data-stkr-i18n]) the sections render from the
     product metafield stk.review_i18n (+ _2, _3) and the shop metafield stk.review_i18n_home, in the
     page language: {"_l": "<lang>", "<uuid>": {"t": title, "b": body, "r": reply, "h": hash}}.
     Shopify serves the English value where a language has no translation yet, so a value counts only
     when its _l is the page language, and an entry only when its h equals
     sha256(title + '|' + body_html + '|' + reply_content)[:16] of the review Judge.me serves now:
     a review edited after we translated it shows its new original. Reviews we have not translated
     go to Judge.me's own machine translation (title and body; 30 per call, 4 s), only while the
     section's setting allows it. A translated review says so and keeps its original one tap away;
     text left in its own language carries a lang attribute. English pages render no island. */
  const LANG = String(document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const REDACTED = '(redacted)';
  const dec = (v) => (LANG === 'en' ? String(v) : String(v).replace('.', ','));
  const srcOf = (r) => { const l = String((r && r.language) || '').toLowerCase().split('-')[0]; return (!l || l === '_blank') ? 'en' : l; };
  /* entities and tags out, parsed in an inert document: nothing in it loads or runs */
  const decode = (s) => {
    s = String(s == null ? '' : s);
    if (!/[&<]/.test(s)) return s.trim();
    s = s.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
    return (new DOMParser().parseFromString(s, 'text/html').body.textContent || '').trim();
  };
  const brs = (s) => esc(s).replace(/\n/g, '<br>');
  const hasWords = (r) => { const t = decode(r.title), b = strip(r.body_html); return (!!t && t !== REDACTED) || (!!b && b !== REDACTED); };
  const hash16 = async (s) => {
    try {
      const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
      return [...new Uint8Array(d)].map((x) => x.toString(16).padStart(2, '0')).join('').slice(0, 16);
    } catch (e) { return ''; }
  };
  let OURS = null;
  const ours = () => {
    if (OURS) return OURS;
    OURS = {};
    document.querySelectorAll('script[data-stkr-i18n]').forEach((s) => {
      try {
        const o = JSON.parse(s.textContent || '{}');
        if (o && o._l === LANG) Object.keys(o).forEach((k) => { if (k !== '_l') OURS[k] = o[k]; });
      } catch (e) { /* a broken island costs only its own entries */ }
    });
    return OURS;
  };
  /* Judge.me's answers carry no cache header (measured 2 Oct), so each one is kept for the visit in
     sessionStorage, per language and per review version (uuid:h): a review is asked for once a visit,
     not on every page that shows it. An answer that is not ok (rate limit, error) is kept as "none" for
     the visit, so a struggling endpoint is not asked again; a timeout or network error is not kept. */
  const JMK = `stkr-jm:${LANG}`;
  let JMC = null;
  const jmCache = () => {
    if (JMC) return JMC;
    try { JMC = JSON.parse(window.sessionStorage.getItem(JMK) || '{}'); } catch (e) { JMC = null; }
    if (!JMC || typeof JMC !== 'object') JMC = {};
    return JMC;
  };
  /* reviews: [{uuid, _hh}]; returns {uuid: {title, body}} for the ones Judge.me answered */
  const judgeme = async (reviews, shop) => {
    const cache = jmCache();
    const key = (r) => `${r.uuid}:${r._hh || ''}`;
    const ask = reviews.filter((r) => !Object.prototype.hasOwnProperty.call(cache, key(r)));
    const chunks = [];
    for (let i = 0; i < ask.length; i += 30) chunks.push(ask.slice(i, i + 30));
    await Promise.all(chunks.map(async (c) => {
      const ctl = window.AbortController ? new AbortController() : null;
      const timer = ctl ? setTimeout(() => ctl.abort(), 4000) : null;
      try {
        const url = `https://api.judge.me/api/review_translations?review_uuids=${c.map((r) => encodeURIComponent(r.uuid)).join(',')}`
          + `&shop_domain=${encodeURIComponent(shop)}&language=${encodeURIComponent(LANG)}&page=1`;
        const res = await fetch(url, ctl ? { signal: ctl.signal } : {});
        const got = (res.ok ? await res.json() : null) || {};
        c.forEach((r) => { const x = got[r.uuid]; cache[key(r)] = x ? { title: x.title, body: x.body } : 0; });
      } catch (e) { /* those reviews keep their original */ }
      if (timer) clearTimeout(timer);
    }));
    if (ask.length) { try { window.sessionStorage.setItem(JMK, JSON.stringify(cache)); } catch (e) { /* asked again next page */ } }
    const out = {};
    reviews.forEach((r) => { const v = cache[key(r)]; if (v) out[r.uuid] = v; });
    return out;
  };
  /* list: Judge.me reviews ({uuid, language, title, body_html, reply_content}) or Liquid tiles
     ({uuid, language, _h}); sets r._t = {t, b, r} for the ones put into the page language
     (a field missing from _t stays original). Never throws: on any failure the originals stay. */
  const localise = async (list, allowJm, shop) => {
    try {
      const need = list.filter((r) => r && r.uuid && srcOf(r) !== LANG);
      if (!need.length) return;
      const mine = ours();
      const gaps = [];
      await Promise.all(need.map(async (r) => {
        const e = mine[r.uuid];
        r._hh = r._h || await hash16(`${r.title || ''}|${r.body_html || ''}|${r.reply_content || ''}`);
        if (e && e.h && r._hh && e.h === r._hh) r._t = { t: e.t, b: e.b, r: e.r };
        else gaps.push(r);
      }));
      if (!gaps.length || !allowJm || !shop) return;
      const jm = await judgeme(gaps, shop);
      const pick = (f) => (f && f.success && f.translated_text
        && String(f.source_language || '').toLowerCase().split('-')[0] !== LANG ? decode(f.translated_text) : undefined);
      gaps.forEach((r) => {
        const x = jm[r.uuid] || {};
        const t = pick(x.title), b = pick(x.body);
        if (t !== undefined || b !== undefined) r._t = { t, b };
      });
    } catch (e) { /* the originals stay */ }
  };
  const note = () => `<p class="stkr__trans">${esc(stkT('stk.reviews.auto_translated', 'Automatically translated'))}</p>`;
  /* a tile Liquid drew (home and collection strips): swap its words in the tile and in its full view,
     keeping the original for "Show original" */
  const swapTile = (el, tr, src) => {
    const tpl = el.querySelector('template.stkr__fulltpl');
    const full = tpl ? tpl.content.querySelector('.stkr__full') : null;
    if (full) {
      const d = document.createElement('details');
      d.className = 'stkr__orig';
      const s = document.createElement('summary');
      s.textContent = stkT('stk.reviews.show_original', 'Show original');
      const box = document.createElement('div');
      box.className = 'stkr__origtext'; box.lang = src;
      const ot = full.querySelector('.stkr__title');
      if (ot && tr.t != null) { const p = document.createElement('p'); const b = document.createElement('b'); b.textContent = ot.textContent; p.appendChild(b); box.appendChild(p); }
      const ob = full.querySelector('.stkr__fullbody');
      if (ob && tr.b != null) { const c = document.createElement('div'); c.innerHTML = ob.innerHTML; box.appendChild(c); }
      const orp = full.querySelector('.stkr__replytext');
      if (orp && tr.r != null) { const c = document.createElement('p'); c.lang = 'en'; c.textContent = orp.textContent; box.appendChild(c); }
      d.append(s, box);
      full.insertBefore(d, full.querySelector(':scope > .stkr__for'));
    }
    [el.querySelector('.stkr__tiletext'), full].forEach((scope) => {
      if (!scope) return;
      const t = scope.querySelector('.stkr__title');
      if (t && tr.t != null) { t.textContent = tr.t; t.removeAttribute('lang'); }
      const b = scope.querySelector('.stkr__body');
      if (b && tr.b != null) { b.textContent = tr.b; b.removeAttribute('lang'); }
      const fb = scope.querySelector('.stkr__fullbody');
      if (fb && tr.b != null) { fb.innerHTML = `<p>${brs(tr.b)}</p>`; fb.removeAttribute('lang'); }
      const rp = scope.querySelector('.stkr__replytext');
      if (rp && tr.r != null) { rp.innerHTML = brs(tr.r); rp.removeAttribute('lang'); }
      const who = scope.querySelector('.stkr__who');
      if (who) who.insertAdjacentHTML('afterend', note());
    });
  };
  const localiseStrip = async (strip) => {
    const list = [...strip.querySelectorAll('.stkr__tile[data-stkr-uuid]')]
      .map((el) => ({ uuid: el.dataset.stkrUuid, language: el.dataset.stkrSrc, _h: el.dataset.stkrH, el }));
    if (!list.length) return;
    await localise(list, strip.dataset.stkrMt !== 'false', strip.dataset.shop || (window.Shopify && window.Shopify.shop) || '');
    list.forEach((r) => { if (r._t) swapTile(r.el, r._t, srcOf(r)); });
  };

  /* The product title's star component, drawn here from the same geometry so
     the two ratings are one design: 94x18 viewBox, five paths, a gradient
     stopped at the score. The gradient runs in user space across the whole
     row: in the default bounding-box units each star took the stop for
     itself, so a 2-star review drew five stars each 40% filled (Thomas,
     5 Sep). (stk-pdp.css .stkp__stars; stk-stars.liquid) */
  let starId = 0;
  const starsSvg = (rating, cls) => {
    const id = `stkrStar${++starId}`;
    const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    /* one path for all five stars, no transforms: a transformed path reads a
       user-space gradient in its own shifted space, which filled every star
       from its own left edge */
    const d = Array.from({ length: 5 }, (_, i) => {
      const x = 9 + i * 19;
      return `M${x} 1.6l2.06 4.55 4.94.53-3.7 3.33 1.05 4.87L${x} 12.4l-4.35 2.48 1.05-4.87-3.7-3.33 4.94-.53z`;
    }).join('');
    const paths = `<path fill="url(#${id})" d="${d}"/>`;
    return `<svg class="stkr__stars ${cls}" width="94" height="18" viewBox="0 0 94 18" role="img" aria-label="${esc(stkT("stk.reviews.rating_out_of_5", '{rating} out of 5').replace('{rating}', dec(rating)))}">
      <defs><linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="94" y2="0">
        <stop offset="${pct}%" stop-color="var(--stkr-star)"/><stop offset="${pct}%" stop-color="var(--stkr-star-off)"/>
      </linearGradient></defs>${paths}</svg>`;
  };

  const CHECK = `<svg class="stkr__check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="${esc(stkT("stk.reviews.verified_buyer", 'Verified buyer'))}"><path d="M12 2.6l2.6 1.9 3.2-.1.9 3.1 2.6 1.8-1.2 3 1.2 3-2.6 1.8-.9 3.1-3.2-.1-2.6 1.9-2.6-1.9-3.2.1-.9-3.1L3.5 16l1.2-3-1.2-3 2.6-1.8.9-3.1 3.2.1z"/><path d="m9 12 2 2 4-4"/></svg>`;

  /* the same '2 Sep 2026' the Liquid tiles print (Chrome's en-GB short month
     is 'Sept', which would differ between the two pages). i18n (P1): the month names and
     the day/month/year order are keys, not hardcoded English, and must stay in step with
     the Liquid tiles' 'stk_review_date' format in each language. */
  const MONTHS = stkT("stk.reviews.months_abbr", 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec').split(',');
  const fmtDate = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return stkT("stk.reviews.date_medium", '{day} {month} {year}')
      .replace('{day}', d.getDate())
      .replace('{month}', MONTHS[d.getMonth()] || '')
      .replace('{year}', d.getFullYear());
  };

  /* ---- the full review, opened from any tile ---------------------------- */

  const openFull = (tile, byKeyboard) => {
    const tpl = tile.querySelector('template.stkr__fulltpl');
    if (!tpl) return;
    let dlg = document.querySelector('.stkr__box');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.className = 'stkr__box';
      dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('.stkr__close')) dlg.close(); });
      /* focus goes back to the tile on close; after a tap that would draw the
         keyboard ring round it, so only a keyboard opener keeps it */
      dlg.addEventListener('close', () => { if (!dlg.dataset.byKeyboard && document.activeElement) document.activeElement.blur(); });
      document.body.appendChild(dlg);
    }
    if (byKeyboard) dlg.dataset.byKeyboard = '1'; else delete dlg.dataset.byKeyboard;
    dlg.replaceChildren(tpl.content.cloneNode(true));
    const x = document.createElement('button');
    x.type = 'button'; x.className = 'stkr__close'; x.setAttribute('aria-label', stkT("stk.reviews.close", 'Close')); x.textContent = '×';
    dlg.appendChild(x);
    dlg.showModal();
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('a')) return;
    const tile = e.target.closest('.stkr__tile');
    if (tile) openFull(tile);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const tile = e.target.closest && e.target.closest('.stkr__tile');
    if (tile && e.target === tile) { e.preventDefault(); openFull(tile, true); }
  });

  /* ---- the strip: one tile every few seconds, hands off when touched ----- */

  const drive = (strip, every, nearEnd) => {
    if (strip.dataset.stkrDriven) return;
    strip.dataset.stkrDriven = '1';
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let timer = null;
    let held = false;
    let seen = true;

    /* where a tile sits in the strip's own scroll space */
    const pos = (t) => t.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
    const after = (cur) => [...strip.querySelectorAll('.stkr__tile')].find((t) => pos(t) > cur + 2);

    /* every move lands with a whole tile at the start, never part of one
       (Thomas, 5 Sep: "the movement should show full card everytime") */
    const step = async () => {
      if (!strip.querySelector('.stkr__tile')) return;
      const cur = strip.scrollLeft;
      let next = after(cur);
      const tiles = strip.querySelectorAll('.stkr__tile');
      if (nearEnd && (!next || next === tiles[tiles.length - 1])) { await nearEnd(); next = after(cur); }
      /* the strip cannot scroll past its end, so a tile that would need it to
         cannot be put at the start; go round to the first one instead of
         landing part way (measured: the last move landed 11-164px short) */
      const target = next ? Math.round(pos(next)) : -1;
      const max = strip.scrollWidth - strip.clientWidth;
      if (next && target > cur + 2 && target <= max + 1) strip.scrollTo({ left: target, behavior: 'smooth' });
      else strip.scrollTo({ left: 0, behavior: 'smooth' });
    };
    const run = () => {
      clearInterval(timer); timer = null;
      if (still || held || !seen) return;
      timer = setInterval(step, every);
    };

    strip.addEventListener('pointerenter', () => { held = true; run(); });
    strip.addEventListener('pointerleave', () => { held = false; run(); });
    strip.addEventListener('focusin', () => { held = true; run(); });
    strip.addEventListener('focusout', () => { held = false; run(); });
    strip.addEventListener('touchstart', () => { held = true; run(); }, { passive: true });
    strip.addEventListener('touchend', () => { setTimeout(() => { held = false; run(); }, 6000); }, { passive: true });
    /* a hand-scrolled strip also needs its next page */
    if (nearEnd) strip.addEventListener('scroll', () => {
      if (strip.scrollLeft + strip.clientWidth * 2 >= strip.scrollWidth) nearEnd();
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((es) => { es.forEach((e) => { seen = e.isIntersecting; run(); }); }, { threshold: 0.3 }).observe(strip);
    } else run();
  };

  /* ---- the product page ------------------------------------------------- */

  class Reviews {
    constructor(root) {
      this.root = root;
      this.page = 0;
      this.pages = 1;
      this.busy = false;
      this.productId = root.dataset.productId;
      // 2 Oct 2026: a product that replaced another (Custom Vinyl Stickers, option B) lists the reviews of the one it
      // replaced (product metafield stickiply.reviews_from, Judge.me product group). The reviews_for_widget endpoint
      // does not apply groups, so it is asked for that product; writing a review still goes to this one.
      this.listId = root.dataset.reviewsProductId || this.productId;
      this.shop = root.dataset.shop;
      this.mt = root.dataset.stkrMt !== 'false';   // P172: the section's setting for Judge.me's machine translation
      this.seen = new Set();
      this.logo = root.dataset.logo || '';
      this.shopName = root.dataset.shopName || 'Stickiply';
      this.strip = root.querySelector('[data-stkr-strip]');
      this.write = root.querySelector('[data-stkr-write]');
      if (this.write) this.write.addEventListener('click', () => this.openWriteForm());
      root.addEventListener('click', (e) => {
        const t = e.target.closest('[data-stkr-img]');
        if (t) this.lightbox(t.dataset.stkrImg);
      });
    }

    url(page) {
      const q = new URLSearchParams({
        product_id: this.listId, page, sort_by: 'created_at', sort_dir: 'desc',
        translation_locale: 'en', skip_other_languages: 'false', widget_theme: 'align',  // P172: every language (the French review was hidden from everyone)
        shop_domain: this.shop, platform: 'shopify',
      });
      return `${ENDPOINT}?${q}`;
    }

    async load() {
      if (this.busy || this.page >= this.pages) return;
      this.busy = true;
      try {
        const data = await (await fetch(this.url(this.page + 1))).json();
        // 2 Oct 2026: with multi-language sorting on, Judge.me returns the reviews in primary_language_reviews
        // (+ other_language_reviews) and its pagination in primary_language_pagination; `reviews` and `pagination`
        // come back empty/absent, so every product showed "No reviews yet". Read both shapes.
        const pag = data.pagination || data.primary_language_pagination;
        this.page = pag ? pag.current_page : this.page + 1;
        this.pages = pag ? pag.total_pages : this.page;
        if (this.page === 1) this.paintSummary(data);
        const list = (data.reviews && data.reviews.length) ? data.reviews
          : [].concat(data.primary_language_reviews || [], data.other_language_reviews || []);
        /* P172: other_language_reviews comes back with every page, so each review is drawn once (by
           uuid), in the page language where we or Judge.me have it */
        const fresh = list.filter((r) => r && (!r.uuid || (!this.seen.has(r.uuid) && this.seen.add(r.uuid))));
        await localise(fresh.filter(hasWords), this.mt, this.shop);
        this.paintReviews(fresh);
      } catch (e) {
        const load = this.root.querySelector('[data-stkr-loading]');
        if (load) load.textContent = stkT("stk.reviews.load_error", 'Reviews could not be loaded.');
        this.pages = this.page;
      }
      this.busy = false;
    }

    /* the first tiles, then enough to fill the strip once */
    async fill() {
      await this.load();
      while (this.page < this.pages && this.strip.scrollWidth <= this.strip.clientWidth) await this.load();
      drive(this.strip, this.every, () => this.load());
    }

    paintSummary(data) {
      const avg = parseFloat(data.average_rating || 0);
      const set = (sel, v) => { const el = this.root.querySelector(sel); if (el) el.textContent = v; };
      /* one decimal at least, so an all-five-star product reads 5.0 beside its
         neighbours' 4.98 rather than a bare 5 */
      const n = data.number_of_reviews || 0;
      set('[data-stkr-avg]', avg ? dec(avg.toFixed(2).replace(/0$/, '')) : '0');   // P172: 4,96 outside English
      set('[data-stkr-count]', n);
      if (!n) {
        /* no score to show yet: the number goes, the stars stay grey, and the
           count line says so in words */
        const num = this.root.querySelector('[data-stkr-avg]'); if (num) num.hidden = true;
        const cnt = this.root.querySelector('.stkr__cnt'); if (cnt) cnt.textContent = stkT("stk.reviews.summary_none_yet", 'No reviews yet');
      }
      const holder = this.root.querySelector('[data-stkr-avgstars]');
      if (holder) holder.outerHTML = starsSvg(avg, 'stkr__stars--xl');

      const medals = this.root.querySelector('[data-stkr-medals]');
      if (medals && (data.product_medals || []).length) {
        medals.innerHTML = data.product_medals.map((m) => `
          <img src="${esc(m.medal_image_url)}" alt="${esc(m.title)}" title="${esc(m.description)}" loading="lazy">`).join('');
        medals.hidden = false;
      }

      /* the photo rail: every customer photo as a rounded square */
      const rail = this.root.querySelector('[data-stkr-photos]');
      const gallery = this.root.querySelector('[data-stkr-gallery]');
      const pics = (data.photo_gallery || []).flatMap((r) => (r.pictures_urls || []))
        .map((u) => (typeof u === 'string' ? { big: u } : { big: u.original || u.huge || u.compact, small: u.small || u.compact }))
        .filter((u) => u.big);
      if (rail && pics.length) {
        rail.insertAdjacentHTML('beforeend', pics.map((u) => `
          <button type="button" class="stkr__shot" data-stkr-img="${esc(u.big)}" aria-label="${esc(stkT("stk.reviews.open_customer_photo", 'Open customer photo'))}">
            <img src="${esc(u.big)}" ${u.small ? `srcset="${esc(u.small)} 160w, ${esc(u.big)} 1024w" sizes="(max-width: 749px) 38vw, 150px"` : ''} loading="lazy" alt=""></button>`).join(''));
        gallery.hidden = false;
      }
    }

    paintReviews(reviews) {
      const load = this.root.querySelector('[data-stkr-loading]');
      if (load) load.remove();
      if (!reviews.length && !this.strip.querySelector('.stkr__tile')) {
        /* a product nobody has rated yet still gets a tile, and it is the
           invitation (Thomas, 5 Sep: "No reviews yet, rate it! with a link to
           write a review") */
        this.strip.insertAdjacentHTML('beforeend', `
          <div class="stkr__empty">
            <p class="stkr__emptyh">${esc(stkT("stk.reviews.empty_heading", 'No reviews yet - rate it!'))}</p>
            <p class="stkr__emptyp">${esc(stkT("stk.reviews.empty_body", 'Bought this one? Two minutes and a photo help the next maker choose.'))}</p>
            <button type="button" class="stkr__write stkr__write--empty">${esc(stkT("stk.reviews.write_a_review", 'Write a review'))}</button>
          </div>`);
        this.strip.querySelector('.stkr__write--empty').addEventListener('click', () => this.openWriteForm());
        return;
      }
      this.strip.insertAdjacentHTML('beforeend', reviews.map((r) => {
        /* a rating with no words is already counted in the score above. P172: an erased review (Judge.me
           blanks it to the word "(redacted)") is never drawn, and titles lose Judge.me's entities (I&#39;m) */
        const title0 = decode(r.title) === REDACTED ? '' : decode(r.title);
        const body0 = strip(r.body_html) === REDACTED ? '' : strip(r.body_html);
        if (!title0 && !body0) return '';
        /* P172: r._t is the review in the page language (localise); what stays original says its language */
        const src = srcOf(r);
        const tr = r._t || null;
        const own = (l) => (l !== LANG ? ` lang="${esc(l)}"` : '');
        const tT = !!(tr && tr.t != null), tB = !!(tr && tr.b != null), tR = !!(tr && tr.r != null && r.reply_content);
        const title = tT ? tr.t : title0;
        const titleAttr = tT ? '' : own(src);
        const bodyAttr = tB ? '' : own(src);
        const pics = (r.pictures_urls || []).map((u) => (typeof u === 'string' ? u : (u.original || u.huge || u.compact))).filter(Boolean);
        /* the thumbnail for a 150px box on a 1x screen, the full size for a
           phone's full-width box or a retina screen: Judge.me serves both */
        const pic0 = (r.pictures_urls || [])[0];
        const srcset = pic0 && typeof pic0 !== 'string' && pic0.small && pic0.huge ? `srcset="${esc(pic0.small)} 160w, ${esc(pic0.huge)} 1024w" sizes="(max-width: 749px) 92vw, 150px"` : '';
        /* Judge.me sends the literal word "Anonymous" for an anonymous reviewer (P172: the key, in every language) */
        const anon = !r.reviewer_name || r.reviewer_name === 'Anonymous' || r.is_anonymous_reviewer;
        const who = `<p class="stkr__who">${r.verified_buyer ? CHECK : ''}<b>${esc(anon ? stkT("stk.reviews.anonymous_default", 'Anonymous') : r.reviewer_name)}</b><span class="stkr__when">${esc(fmtDate(r.created_at))}</span>${r.reply_content ? `<span class="stkr__replied">${esc(stkT("stk.reviews.replied_badge", 'Replied'))}</span>` : ''}</p>`;
        const said = tr ? note() : '';
        /* P172: the page language's own product URL (Judge.me sends /products/<english-handle>); prices() then
           follows its redirect and puts in the translated name */
        const link = ROOT === '/' ? r.product_url : ROOT.replace(/\/$/, '') + r.product_url;
        const product = r.product_url && r.product_title ? `
            <a class="stkr__for" href="${esc(link)}">
              ${r.product_variant_image_url ? (() => { const u = String(r.product_variant_image_url).split('?')[0]; return `<img src="${esc(u)}?width=60" srcset="${esc(u)}?width=60 1x, ${esc(u)}?width=120 2x" width="30" height="30" alt="" loading="lazy">`; })() : ''}
              <span class="stkr__forwords"><span class="stkr__forname">${esc(r.product_title)}</span><span class="stkr__forprice" data-stkr-price="${esc(r.product_url)}"></span></span>
            </a>` : '';
        const reply = tR ? `<p>${brs(tr.r)}</p>` : (LANG !== 'en' ? `<div lang="en">${clean(r.reply_content)}</div>` : clean(r.reply_content));
        const orig = tr ? `<details class="stkr__orig"><summary>${esc(stkT('stk.reviews.show_original', 'Show original'))}</summary><div class="stkr__origtext" lang="${esc(src)}">${tT && title0 ? `<p><b>${esc(title0)}</b></p>` : ''}${tB && body0 ? `<div>${clean(r.body_html)}</div>` : ''}${tR ? `<div lang="en">${clean(r.reply_content)}</div>` : ''}</div></details>` : '';
        return `
        <div class="stkr__tile" tabindex="0" role="button" aria-label="${esc(stkT("stk.reviews.open_this_review", 'Open this review'))}">
          ${pics.length ? `<div class="stkr__tileimg"><img src="${esc(pics[0])}" ${srcset} loading="lazy" alt=""></div>` : ''}
          <div class="stkr__tiletext">
            ${starsSvg(r.rating, 'stkr__stars--sm')}
            ${title ? `<h3 class="stkr__title"${titleAttr}>${esc(title)}</h3>` : ''}
            ${who}${said}
            <div class="stkr__body"${bodyAttr}>${esc(tB ? tr.b : body0)}</div>
            ${product}
          </div>
          <template class="stkr__fulltpl"><div class="stkr__full">
            ${starsSvg(r.rating, 'stkr__stars--sm')}
            ${title ? `<h3 class="stkr__title"${titleAttr}>${esc(title)}</h3>` : ''}
            ${who}${said}
            <div class="stkr__fullbody"${bodyAttr}>${tB ? `<p>${brs(tr.b)}</p>` : (body0 ? clean(r.body_html) : '')}</div>
            ${pics.length ? `<div class="stkr__fullpics">${pics.map((u) => `<img src="${esc(u)}" loading="lazy" alt="">`).join('')}</div>` : ''}
            ${r.reply_content ? `<div class="stkr__reply">
              <p class="stkr__replyby">${this.logo ? `<img src="${esc(this.logo)}" alt="${esc(this.shopName)}" loading="lazy">` : ''}<span>${esc(stkT("stk.reviews.replied_badge", 'Replied'))}</span></p>
              ${reply}
            </div>` : ''}
            ${orig}
            ${product}
          </div></template>
        </div>`;
      }).join(''));
      this.prices();
    }

    /* the product card carries a price, as the add-on card does. One fetch per
       product, from the storefront's own product JSON. */
    async prices() {
      Reviews.cache = Reviews.cache || {};
      const slots = [...this.root.querySelectorAll('[data-stkr-price]:empty')];
      const wanted = [...new Set(slots.map((s) => s.dataset.stkrPrice))];
      Reviews.names = Reviews.names || {};
      await Promise.all(wanted.map(async (url) => {
        if (Reviews.cache[url] !== undefined) return;
        try {
          /* P172: outside the primary language, Judge.me's English handle is followed to the page language's
             own (/fr/products/<english-handle> redirects there; its .js is a 404), and that product's JSON
             gives the chip its name in the page language */
          let path = url;
          if (ROOT !== '/') path = new URL((await fetch(ROOT.replace(/\/$/, '') + url, { method: 'HEAD' })).url).pathname;
          const d = await (await fetch(`${path}.js`)).json();
          const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
          const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cur }).format(d.price_min / 100);
          Reviews.cache[url] = d.price_varies ? stkT("stk.reviews.from_price_prefix", 'From {money}').replace('{money}', money) : money;
          if (ROOT !== '/' && d.title) Reviews.names[url] = { title: d.title, path };
        } catch (e) { Reviews.cache[url] = ''; }
      }));
      slots.forEach((s) => { s.textContent = Reviews.cache[s.dataset.stkrPrice] || ''; });
      if (ROOT === '/') return;
      const rename = (scope) => scope.querySelectorAll('a.stkr__for [data-stkr-price]').forEach((s) => {
        const n = Reviews.names[s.dataset.stkrPrice];
        const a = s.closest('a.stkr__for');
        if (!n || !a) return;
        a.setAttribute('href', n.path);
        const nm = a.querySelector('.stkr__forname');
        if (nm) nm.textContent = n.title;
      });
      rename(this.root);
      this.root.querySelectorAll('template.stkr__fulltpl').forEach((tp) => rename(tp.content));
    }

    /* Judge.me's own write-review modal, opened the way its widget opens it
       (read from ReviewWidgetManager.handleWriteReview: a new _WriteReviewModal
       on jdgm.$, setup with the product id, then showModalPage). If the app's
       script is not on the page, the hosted form opens in a new tab, built
       the way widget_form.js builds it. */
    async openWriteForm() {
      const J = window.jdgm;
      try {
        if (J && typeof J._WriteReviewModal === 'function' && typeof J.$ === 'function') {
          document.querySelectorAll('.jdgm-review-widget-modal.jdgm-write-review-modal').forEach((m) => m.remove());
          const modal = new J._WriteReviewModal(J.$);
          if (await modal.setup('jdgm-review-widget-modal', this.productId, { siwsSignedIn: false })) { modal.showModalPage(); return; }
        }
      } catch (e) { /* fall through to the hosted form */ }
      const host = ((J && J.API_HOST) || 'https://judge.me').replace(/\/$/, '');
      const q = new URLSearchParams({ shop_domain: this.shop, platform: 'shopify', product_id: this.productId });
      window.open(`${host}/storefront_reviews/new?${q}`, '_blank', 'noopener,noreferrer');
    }

    lightbox(src) {
      let dlg = document.querySelector('.stkr__box');
      if (!dlg) {
        dlg = document.createElement('dialog');
        dlg.className = 'stkr__box';
        dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('.stkr__close')) dlg.close(); });
        document.body.appendChild(dlg);
      }
      dlg.innerHTML = `<img class="stkr__boximg" src="${esc(src)}" alt=""><button type="button" class="stkr__close" aria-label="${esc(stkT("stk.reviews.close", 'Close'))}">×</button>`;
      dlg.showModal();
    }
  }

  const start = (root) => {
    const r = new Reviews(root);
    r.every = Math.max(2000, (parseFloat(root.dataset.every) || 5) * 1000);
    /* the write button is part of the card from the start: it always has
       somewhere to go (the app's modal, or Judge.me's hosted form) */
    if (r.write) r.write.hidden = false;
    r.fill();
  };

  document.querySelectorAll('[data-stkr]').forEach((root) => {
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { io.disconnect(); start(root); } });
      }, { rootMargin: '600px' });
      io.observe(root);
    } else start(root);
  });

  /* strips rendered in Liquid (the home page) */
  document.querySelectorAll('[data-stkr-strip]').forEach((strip) => {
    if (strip.closest('[data-stkr]')) return;
    const every = Math.max(2000, (parseFloat(strip.dataset.every) || 5) * 1000);
    drive(strip, every, null);
    localiseStrip(strip);   // P172: tiles in another language, into the page language
  });
})();
